<template>
  <div :class="['sidebar', { collapsed: isCollapsed }]">
    <!-- Header -->
    <div class="sidebar-header">
      <a href="/">
        <img 
          v-if="!isCollapsed" 
          src="/images/logo-white.png" 
          alt="Ionaute" 
          class="logo" 
        />
        <img 
          v-if="isCollapsed" 
          src="/images/logo-min-white.png" 
          alt="Ionaute" 
          class="logo-collapsed"
        />
      </a>
      <div class="badge-container">
        <p v-if="environment === 'staging'" class="badge badge-warning"> Staging </p>
        <p v-if="environment === 'local'" class="badge badge-danger"> Dev </p>
      </div>

      <!-- Toggle Button -->
      <button class="toggle-btn" @click="toggleSidebar">
        <font-awesome-icon 
          :icon="isCollapsed ? 'fa-solid fa-chevron-right' : 'fa-solid fa-chevron-left'" 
        />
      </button>
    </div>


    <!-- Menu -->
    <ul class="menu">
      <li v-for="module in filteredModules" :key="module.name" class="menu-item">
        <!-- Si le module a une seule page, affiche un lien direct -->
        <template v-if="module.pages.length === 1">
          <a 
            :href="routeUrl(module.pages[0].link)"
            class="menu-header menu-single"
            :class="{ active: isActiveRoute(module.pages[0].activeLink) }"
          >
            <div class="menu-header-content">
              <font-awesome-icon :icon="module.icon" class="menu-icon" />
              <span v-if="!isCollapsed">{{ module.name }}</span>
            </div>
          </a>
        </template>

        <!-- Si le module a plusieurs pages, affiche le sous-menu -->
        <template v-else>
          <div
            class="menu-header"
            @click="toggleSection(module.name)"
            :class="{ active: module.open || isSectionActive(module) }"
          >
            <div class="menu-header-content">
              <font-awesome-icon :icon="module.icon" class="menu-icon" />
              <span v-if="!isCollapsed">{{ module.name }}</span>
            </div>
            <font-awesome-icon
              v-if="!isCollapsed"
              :icon="module.open ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'"
            />
          </div>
          <ul v-if="module.open && !isCollapsed" class="submenu">
            <li
              v-for="page in module.pages"
              :key="page.name"
              :class="{ active: isActiveRoute(page.activeLink) }"
            >
              <a :href="routeUrl(page.link)">
                {{ page.name }}
              </a>
            </li>
          </ul>
        </template>
      </li>
    </ul>

    <!-- Footer -->
    <div class="sidebar-footer">
      <p class="version">v{{ appVersion }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "Sidebar",
  props: {
    environment: {
      type: String,
      default: "production"
    },
    isAuth: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      default: () => ({})
    },
    permissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isCollapsed: false, // État de la sidebar
      appVersion: "1.5.0",
      modules: [
        {
          name: "Dashboard",
          icon: "fa-solid fa-tachometer-alt",
          open: false,
          pages: [
            {
              name: "Overview",
              link: "index",
              activeLink: "index",
              permission: null
            }
          ]
        },
        {
          name: "Smart Vault",
          icon: "fa-solid fa-list",
          open: false,
          pages: [
            {
              name: "Product library",
              link: "smart-vault.index",
              activeLink: "smart-vault.*",
              permission: "view smart-vault"
            }
          ]
        },
        {
          name: "Purchase Orders",
          icon: "fa-solid fa-shopping-cart",
          open: false,
          pages: [
            {
              name: "Manage Orders",
              link: "orders.index",
              activeLink: "orders.*",
              permission: "manage orders"
            },
            {
              name: "Orders import",
              link: "imports.index",
              activeLink: "imports.*",
              permission: "manage orders imports"
            }
          ]
        },
        {
          name: "Quality Control",
          icon: "fa-solid fa-chart-line",
          open: false,
          pages: [
            {
              name: "QC Reports",
              link: "reports.index",
              activeLink: "reports.*",
              permission: null
            },
            {
              name: "Report templates",
              link: "settings.templates.index",
              activeLink: "settings.templates.*",
              permission: "manage report templates"
            },
            {
              name: "Report custom fields",
              link: "settings.columns.index",
              activeLink: "settings.columns.*",
              permission: "manage report columns"
            }
          ]
        },
        {
          name: "Analytics",
          icon: "fa-solid fa-chart-pie",
          open: false,
          pages: [
            {
              name: "Analytics dashboard",
              link: "analytics.index",
              activeLink: "analytics.*",
              permission: "view analytics"
            }
          ]
        },
        {
          name: "Marketing Tool",
          icon: "fa-solid fa-rectangle-ad",
          open: false,
          pages: [
            {
              name: "Brand stories",
              link: "brand-stories.index",
              activeLink: "brand-stories.*",
              permission: "manage brand stories"
            },
            {
              name: "To be announced",
              link: "index",
              activeLink: "null",
              permission: "manage brand stories"
            }
          ]
        },
        {
          name: "Passports",
          icon: "fa-solid fa-passport",
          open: false,
          pages: [
            {
              name: "Passport list",
              link: "passports.index",
              activeLink: "passports.*",
              permission: "view all passports"
            }
          ]
        },
        {
          name: "Companies and Users",
          icon: "fa-solid fa-users",
          open: false,
          pages: [
            {
              name: "Companies",
              link: "companies.index",
              activeLink: "companies.*",
              permission: "manage companies"
            },
            {
              name: "Users",
              link: "users.index",
              activeLink: "users.*",
              permission: "manage users"
            }
          ]
        },
        {
          name: "Settings",
          icon: "fa-solid fa-cog",
          open: false,
          pages: [
            {
              name: "Roles & Permissions",
              link: "settings.roles.index",
              activeLink: "settings.roles.*",
              permission: "manage roles"
            },
            {
              name: "Product attributes",
              link: "settings.product-attributes.index",
              activeLink: "settings.product-attributes.*",
              permission: "manage product attributes"
            },
            {
              name: "Problems & categories",
              link: "settings.problems.index",
              activeLink: "settings.problems.*",
              permission: "manage problem categories"
            },
            {
              name: "Problems departments",
              link: "settings.problem-department.index",
              activeLink: "settings.problem-department.*",
              permission: "manage problem categories"
            }
          ]
        }
      ]
    };
  },
  computed: {
    filteredModules() {
      return this.modules
        .map(module => ({
          ...module,
          open: module.open || this.isSectionActive(module), // Ouvre le module si une page est active
          pages: module.pages.filter(
            page => !page.permission || this.can(page.permission)
          )
        }))
        .filter(module => module.pages.length > 0);
    }
  },
  created() {
    this.$root.$on("collapseSidebar", val => {
      this.isCollapsed = val;
    });
  },
  methods: {
    isActiveRoute(routeName) {
      return route().current(routeName);
    },
    isSectionActive(module) {
      return module.pages.some(page => this.isActiveRoute(page.activeLink));
    },
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    toggleSection(moduleName) {
      this.modules = this.modules.map(
        module =>
          module.name === moduleName
            ? { ...module, open: !module.open }
            : module
      );
    },
    can(permissionName) {
      return this.permissions.some(
        permission => permission.name === permissionName
      );
    },
    routeUrl(routeName) {
      return route(routeName); // Génère l'URL complète
    }
  }
};
</script>
